import { connect } from 'react-redux';
import autobind from 'core-decorators/lib/autobind';

import { getNewRelic } from 'shared/analytics/new-relic';
import bi from 'shared/bi/bi';

import { WixConnect } from 'shared/utils/wix-connect';
import memoizedPartial from 'shared/utils/memoized-partial';
import { openMobileOverlay } from 'shared/utils/open-overlay';
import { playerSizeMobileMainItemForNewUX } from 'widget/utils/videos-sizes';
import { storeForReload } from 'widget/utils/reload';

import { canPlayFullOrPreview } from 'widget/ui-selectors/video-access';
import { getAllSettings } from 'shared/selectors/app-settings';
import { getVideosGroupedByIds } from 'shared/selectors/videos';
import { getMainVideoId } from 'widget/ui-selectors/selected-video-id';
import { isVideoPlayingOptimistic } from 'widget/selectors/video-playback-status';
import { getChannelForWidget } from 'shared/selectors/channels';
import { getCurrentSiteUser } from 'widget/selectors/current-site-user';
import { canPlayVideoInFrame } from './ui-selectors/can-play-video-in-frame';

import { openVideoPage } from 'widget/redux/client/actions/mobile-overlay-openners/main-openners';
import { selectVideo, resetSelectedVideo } from 'widget/redux/client/actions/select-video';
import { playVideo, pauseVideo } from 'widget/redux/client/actions/change-playback-status';

import { MOBILE_WIDGET_DATA_KEY } from 'widget/data-components/lazy-channel-videos';
import BaseView from 'widget/views/_base';

import MainWidgetMobilePlayer from 'widget/components/players/main-widget-mobile-player';
import SlidableListWithLazyLoad from 'widget/components/slidable-list-with-lazy-load/slidable-list-with-lazy-load';
import PaymentEvents from 'shared/components/payment-events/payment-events';
import ReloadStorage from 'widget/components/reload-storage/reload-storage';
import CoverWithOverlay from 'widget/components/cover-with-overlay/mobile-cover-with-overlay';

import styles from './mobile.styl';


@connect((state, ownProps) => ({
  isVideoPlaying: isVideoPlayingOptimistic(state),
  currentChannelId: state.currentChannelId,
  canShowChannelCover: !state.isVideosTouched,
  appSettings: getAllSettings(state),
  videoByIds: getVideosGroupedByIds(state),
  firstChannelVideoId: state.firstChannelVideoId,
  videoIds: state.lazyVideoLists.mobileWidget.videoIds,
  fetchVideos: ownProps.lazyChannelVideos.loadFirstPage,
  isVideosFetching: ownProps.lazyChannelVideos.isFetching,
  channelData: getChannelForWidget(state),
  currentSiteUser: getCurrentSiteUser(state),

  mainVideoId: getMainVideoId(state, MOBILE_WIDGET_DATA_KEY),
  canPlayVideoInFrame: canPlayVideoInFrame(state, MOBILE_WIDGET_DATA_KEY),
}), {
  openVideoPage,
  selectVideo,
  resetSelectedVideo,
  playVideo,
  pauseVideo,
})
@WixConnect(styles)
export default class MobileView extends BaseView {

  static displayName = 'MobileView';

  static propTypes = {
    ...BaseView.propTypes,
    lazyChannelVideos: React.PropTypes.object,
    openVideoPage: React.PropTypes.func,
    canPlayVideoInFrame: React.PropTypes.bool,
    biEvent: React.PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      isInitialVideosLoaded: false,
    };

    getNewRelic().setCustomAttribute('layout', 'mobile');
  }

  get mainVideoList() {
    return this.props.lazyChannelVideos;
  }

  get playerSize() {
    const { appSettings } = this.props;
    const widgetWidth = appSettings.requestedWidgetSize.width;

    return playerSizeMobileMainItemForNewUX(widgetWidth);
  }

  @autobind
  openMemberOnlyPage(id) {
    bi.logWidgetSystem('video.memberOnlyInfo.mobileOverlay.requested');
    openMobileOverlay(`#/membership/${id}`);
  }

  @autobind
  processDataFromStorage({ selectedVideoId }) {
    if (selectedVideoId) {
      this.selectVideo({ id: selectedVideoId });
    }
  }

  @autobind
  storeDataForReload() {
    storeForReload({
      selectedVideoId: this.props.mainVideoId,
    });
  }

  @autobind
  playSelectedVideo() {
    const { channelData, currentSiteUser, selectVideo, playVideo, biEvent } = this.props;

    const videoItem = this.mainVideo;
    const { id, videoSource } = videoItem;
    const isPlayAllowed = canPlayFullOrPreview(channelData, videoItem, currentSiteUser);

    if (isPlayAllowed) {
      biEvent('widget.vid.clicked', {
        channelID: channelData.id,
        videoID: id,
        videoType: videoSource,
      });

      playVideo(videoItem.id);
    } else {
      selectVideo(videoItem.id);
    }
  }

  @autobind
  onCurrentSlideChanged(newIndex) {
    const { videoIds } = this.props;
    this.selectVideo({ id: videoIds[newIndex] });
  }

  get noVideoPlayerOverlay() {
    const { width, height } = this.playerSize;
    const { appSettings, channelData } = this.props;

    const props = {
      appSettings,
      channelData,
      showChannelCover: true,
      videoItem: {},
    };

    return (
      <CoverWithOverlay {...props}
                        itemWidth={width}
                        itemHeight={height} />
    );
  }

  @autobind
  onCoverPlayClickInNewUI() {
    // TODO: move to action
    const { canPlayVideoInFrame, openVideoPage, mainVideoId } = this.props;
    if (canPlayVideoInFrame) {
      this.playSelectedVideo();
    } else {
      openVideoPage(mainVideoId);
    }
  }

  renderCoverForCarousel(videoItem, index, className) {
    const { width, height } = this.playerSize;
    const featuredVideoId = _.get(this.props.channelData, 'featuredItem');
    const isInitialVideo = featuredVideoId ? featuredVideoId === videoItem.id : index === 0;
    const showChannelCover = this.canShowChannelCover && isInitialVideo;

    return (
      <CoverWithOverlay videoItem={videoItem}
                        className={className}
                        showChannelCover={showChannelCover}
                        itemWidth={width}
                        itemHeight={height}
                        onSignInRequested={this.showLogInForm}
                        onPlayClick={this.onCoverPlayClickInNewUI}
                        onMemberSignUp={memoizedPartial(this.openMemberOnlyPage, videoItem.id)} />
    );
  }

  get coversListForCarousel() {
    const { videoIds, videoByIds } = this.props;

    return _.map(videoIds, (videoId, index) => (
      <div key={videoId}>
        {this.renderCoverForCarousel(videoByIds[videoId], index, styles['overlay-item-topmost'])}
      </div>
    ));
  }

  get singleItemCover() {
    const videoItem = this.mainVideo;
    return (
      <div styleName="single-item-cover">
        {this.renderCoverForCarousel(videoItem, 0)}
      </div>
    );
  }

  get coversList() {
    const { loadMore } = this.mainVideoList;
    const { videoIds } = this.props;
    const videoItem = this.mainVideo;

    if (_.size(videoIds) === 1) {
      return this.singleItemCover;
    }

    return (
      <ReloadStorage onReload={this.processDataFromStorage}>
        <SlidableListWithLazyLoad loadMore={loadMore}
                                  itemHeight={this.playerSize.height}
                                  styleName="players-list-slidable"
                                  onCurrentSlideChanged={this.onCurrentSlideChanged}
                                  selectedVideoId={videoItem && videoItem.id}>
          {this.coversListForCarousel}
        </SlidableListWithLazyLoad>
      </ReloadStorage>
    );
  }

  get mainUI() {
    const { isVideoPlaying, channelData, canPlayVideoInFrame } = this.props;
    const { width, height } = this.playerSize;
    const videoItem = this.mainVideo;

    if (!videoItem) {
      return this.noVideosPlayerContent;
    }

    return (
      <div data-hook="main-ui"
           styleName="new-mobile-ui-container"
           style={{ width, height }}>
        <div styleName="real-player-placeholder">
          {canPlayVideoInFrame &&
            (<MainWidgetMobilePlayer videoItem={videoItem}
                                     channelData={channelData}
                                     width={width}
                                     height={height}
                                     showPoster={false}
                                     paused={!isVideoPlaying}
                                     onMemberSignUp={this.openMemberOnlyPage} />)
          }
        </div>
        {this.coversList}
      </div>
    );
  }

  @autobind
  reset() {
    this.props.resetSelectedVideo();
  }

  render() {
    if (!this.isUIBlockingDataLoaded) {
      return this.spinner;
    }

    return (
      <section styleName="container"
               className="qa-widget-container">
        <PaymentEvents onSubscription={this.reset}
                       onSubscriptionCancel={this.reset} />
        {this.mainUI}
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/views/mobile/mobile.js